import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Button,
  Badge,
  Table,
  Typography,
  Input,
  Select,
  DatePicker,
  Space,
  Col,
  Row,
  Alert,
  Modal,
  message,
} from "antd";
import LinearProgress from "@material-ui/core/LinearProgress";
import { useAuth0 } from "../../react-auth0-spa";
import config from "../../api_config.json";
import { useAppContext } from "../../context";
import { IdcardOutlined, SearchOutlined } from "@ant-design/icons";
import Highlighter from "react-highlight-words";
import { Link, useHistory } from "react-router-dom";
import { useLocalStorage } from "@rehooks/local-storage";
import { format } from "date-fns";

const { Title } = Typography;
const { RangePicker } = DatePicker;

const initialTableFilters = {
  name: "",
  companyName: "",
  plan: undefined,
  createdAtRange: null,
  couponCode: "",
};

const formatDateTime = (dateValue) => {
  try {
    return format(new Date(dateValue), "dd/MM/yyyy HH:mm");
  } catch (err) {
    console.error(err);
    return "Data mancante o invalida";
  }
};

const includesNormalized = (value, query) =>
  String(value || "")
    .toLowerCase()
    .includes(String(query || "").trim().toLowerCase());

const getDateTime = (dateValue) => {
  if (!dateValue) {
    return null;
  }

  const time = new Date(dateValue).getTime();
  return Number.isFinite(time) ? time : null;
};

const Cards = () => {
  const { isProd } = useAppContext();
  const env = isProd ? "prod" : "dev";
  const [visible, setVisible] = useState(false);
  const [loading, setLoading] = useState(true);
  const [searchText, setSearchText] = useState("");
  const [searchedColumn, setSearchedColumn] = useState("");
  const [items, setItems] = useLocalStorage(`${env}.orgs`, []);
  const [tableFilters, setTableFilters] = useState(initialTableFilters);
  const history = useHistory();

  const { getTokenSilently } = useAuth0();

  const patchCard = async (slug, card) => {
    const token = await getTokenSilently();
    const response = await fetch(
      `${config.baseUrl[env]}/admin?${new URLSearchParams({ slug })}`,
      {
        method: "PATCH",
        body: JSON.stringify({ card, forceNotification: false }),
        headers: {
          "Content-type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      }
    );
    const responseData = await response.json();

    if (!response.ok) {
      throw new Error(responseData.message || "Aggiornamento card non riuscito");
    }

    return responseData.card;
  };

  const deleteCard = async (slug) => {
    const token = await getTokenSilently();
    const response = await fetch(
      `${config.baseUrl[env]}/admin?${new URLSearchParams({ slug })}`,
      {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    const responseData = await response.json();

    if (!response.ok) {
      throw new Error(responseData.message || "Eliminazione card non riuscita");
    }

    return responseData;
  };

  const updateCardInList = (slug, updatedCard) => {
    setItems(
      items.map((item) =>
        item.slug === slug
          ? {
              ...item,
              ...updatedCard,
              lastUpdate: updatedCard.updatedAt || item.lastUpdate,
            }
          : item
      )
    );
  };

  const removeCardFromList = (slug) => {
    setItems(items.filter((item) => item.slug !== slug));
  };

  const fetchData = useCallback(
    async (params = { size: 100 }) => {
      try {
        const token = await getTokenSilently();

        let url = `${config.baseUrl[env]}/admin`;

        if (Object.keys(params).length > 0) {
          url = `${url}?` + new URLSearchParams(params);
        }

        const response = await fetch(url, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        const responseData = await response.json();

        return responseData;
      } catch (error) {
        console.error(error);
        throw error;
      }
    },
    [getTokenSilently, env]
  );

  useEffect(() => {
    setLoading(true);
    env === "dev" ? setVisible(true) : setVisible(false);

    const params = {
      size: 5000,
    };

    fetchData(params).then((response) => {
      const enrichedItems = response.data.map((item) => {
        const newItem = {
          ...item,
          lastUpdate: item.updatedAt ? item.updatedAt : item.createdAt,
        };
        if (item.createdAt) {
          item.createdAt = new Date(item.createdAt);
        }
        if (item.updatedAt) {
          item.updatedAt = new Date(item.updatedAt);
        }
        return newItem;
      });
      setItems(enrichedItems);
      setLoading(false);
    });
  }, [fetchData, setItems, env]);

  const handleSearch = (selectedKeys, confirm, dataIndex) => {
    confirm();
    setSearchText(selectedKeys[0]);
    setSearchedColumn(dataIndex);
  };

  const handleReset = (clearFilters) => {
    clearFilters();
    setSearchText("");
  };

  const updateTableFilter = (name, value) => {
    setTableFilters((current) => ({
      ...current,
      [name]: value,
    }));
  };

  const resetTableFilters = () => {
    setTableFilters(initialTableFilters);
  };

  const filteredItems = useMemo(() => {
    const nameQuery = tableFilters.name.trim();
    const companyQuery = tableFilters.companyName.trim();
    const couponQuery = tableFilters.couponCode.trim();
    const [createdFrom, createdTo] = tableFilters.createdAtRange || [];
    const createdFromTime = createdFrom
      ? createdFrom.clone().startOf("day").valueOf()
      : null;
    const createdToTime = createdTo
      ? createdTo.clone().endOf("day").valueOf()
      : null;

    return items.filter((item) => {
      const fullName = [item.firstName, item.lastName].filter(Boolean).join(" ");
      const itemPlan = item.plan || "gratis";
      const createdTime = getDateTime(item.createdAt);

      if (nameQuery && !includesNormalized(fullName, nameQuery)) {
        return false;
      }

      if (companyQuery && !includesNormalized(item.companyName, companyQuery)) {
        return false;
      }

      if (tableFilters.plan && itemPlan !== tableFilters.plan) {
        return false;
      }

      if (couponQuery && !includesNormalized(item.couponCode, couponQuery)) {
        return false;
      }

      if (createdFromTime && (!createdTime || createdTime < createdFromTime)) {
        return false;
      }

      if (createdToTime && (!createdTime || createdTime > createdToTime)) {
        return false;
      }

      return true;
    });
  }, [items, tableFilters]);

  const getColumnSearchProps = (dataIndex) => ({
    filterDropdown: ({
      setSelectedKeys,
      selectedKeys,
      confirm,
      clearFilters,
    }) => (
      <div style={{ padding: 8 }}>
        <Input
          ref={(node) => {
            // setSearchInput(node);
          }}
          placeholder={`Search ${dataIndex}`}
          value={selectedKeys[0]}
          onChange={(e) =>
            setSelectedKeys(e.target.value ? [e.target.value] : [])
          }
          onPressEnter={() => handleSearch(selectedKeys, confirm, dataIndex)}
          style={{ width: 188, marginBottom: 8, display: "block" }}
        />
        <Space>
          <Button
            type="primary"
            onClick={() => handleSearch(selectedKeys, confirm, dataIndex)}
            icon={<SearchOutlined />}
            size="small"
            style={{ width: 90 }}
          >
            Search
          </Button>
          <Button
            onClick={() => handleReset(clearFilters)}
            size="small"
            style={{ width: 90 }}
          >
            Reset
          </Button>
        </Space>
      </div>
    ),
    filterIcon: (filtered) => (
      <SearchOutlined style={{ color: filtered ? "#1890ff" : undefined }} />
    ),
    onFilter: (value, record) =>
      record[dataIndex]
        ? record[dataIndex]
            .toString()
            .toLowerCase()
            .includes(value.toLowerCase())
        : "",
    onFilterDropdownVisibleChange: (visible) => {
      if (visible) {
        setTimeout(() => {
          // if (searchInput) {
          //   searchInput.select()
          // }
        }, 100);
      }
    },
    render: (text) =>
      searchedColumn === dataIndex ? (
        <Highlighter
          highlightStyle={{ backgroundColor: "#ffc069", padding: 0 }}
          searchWords={[searchText]}
          autoEscape
          textToHighlight={text ? text.toString() : ""}
        />
      ) : (
        text
      ),
  });

  const columns = [
    {
      title: "Impresa",
      dataIndex: "companyName",
      key: "companyName",
      ...getColumnSearchProps("companyName"),
      sorter: {
        compare: (a, b) => {
          if (a.companyName < b.companyName) {
            return -1;
          }
          if (a.companyName > b.companyName) {
            return 1;
          }
          return 0;
        },
      },
    },
    {
      title: "Nome",
      dataIndex: "firstName",
      key: "firstName",
      ...getColumnSearchProps("firstName"),
      render: (firstName, record) => {
        return <Link to={{ pathname: record?.slug ? `/cards/${record.slug}` : "/new", record }}>{firstName}</Link>;
      },
      sorter: {
        compare: (a, b) => {
          if (a.firstName < b.firstName) {
            return -1;
          }
          if (a.firstName > b.firstName) {
            return 1;
          }
          return 0;
        },
      },
    },
    {
      title: "Cognome",
      dataIndex: "lastName",
      key: "lastName",
      ...getColumnSearchProps("lastName"),
      render: (lastName, record) => {
        return <Link to={{ pathname: record?.slug ? `/cards/${record.slug}` : "/new", record }}>{lastName}</Link>;
      },
      sorter: {
        compare: (a, b) => {
          if (a.lastName < b.lastName) {
            return -1;
          }
          if (a.lastName > b.lastName) {
            return 1;
          }
          return 0;
        },
      },
    },
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
      ...getColumnSearchProps("email"),
      render: (email, record) => {
        return <Link to={{ pathname: record?.slug ? `/cards/${record.slug}` : "/new", record }}>{email}</Link>;
      },
      sorter: {
        compare: (a, b) => {
          const emailA = a.email || "";
          const emailB = b.email || "";

          if (emailA < emailB) {
            return -1;
          }
          if (emailA > emailB) {
            return 1;
          }
          return 0;
        },
      },
    },
    {
      title: "Ultimo Aggiornamento",
      dataIndex: "lastUpdate",
      key: "lastUpdate",
      render: formatDateTime,
      sorter: {
        compare: (a, b) => {
          if (a.lastUpdate < b.lastUpdate) {
            return -1;
          }
          if (a.lastUpdate > b.lastUpdate) {
            return 1;
          }
          return 0;
        },
      },
      defaultSortOrder: "descend",
    },
    {
      title: "Creazione",
      dataIndex: "createdAt",
      key: "createdAt",
      render: formatDateTime,
      sorter: {
        compare: (a, b) => {
          const createdA = getDateTime(a.createdAt) || 0;
          const createdB = getDateTime(b.createdAt) || 0;
          return createdA - createdB;
        },
      },
    },
    {
      title: "Piano",
      dataIndex: "plan",
      key: "plan",
      render: (plan, record) => {
        const isPlus = plan === "plus";
        const text = isPlus ? "Plus" : "Gratis";
        return (
          <Badge
            color={isPlus ? "#1b8395" : "#6574a1"}
            text={`${text}${record.planStatus ? ` · ${record.planStatus}` : ""}`}
          />
        );
      },
      filters: [
        { text: "Gratis", value: "gratis" },
        { text: "Plus", value: "plus" },
      ],
      onFilter: (value, record) => (record.plan || "gratis") === value,
    },
    {
      title: "Coupon",
      dataIndex: "couponCode",
      key: "couponCode",
      ...getColumnSearchProps("couponCode"),
      render: (couponCode, record) => {
        if (!couponCode) {
          return <span style={{ color: "#bfbfbf" }}>-</span>;
        }

        return (
          <Space direction="vertical" size={0}>
            <Badge color="#722ed1" text={couponCode} />
            {record.couponName && (
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                {record.couponName}
              </Typography.Text>
            )}
          </Space>
        );
      },
      sorter: {
        compare: (a, b) =>
          String(a.couponCode || "").localeCompare(String(b.couponCode || "")),
      },
    },
    {
      title: "Stato",
      dataIndex: "isDisabled",
      key: "isDisabled",
      render: (isDisabled) => (
        <Badge
          color={isDisabled ? "#d46b08" : "#389e0d"}
          text={isDisabled ? "Disattivata" : "Attiva"}
        />
      ),
      filters: [
        { text: "Attiva", value: "active" },
        { text: "Disattivata", value: "disabled" },
      ],
      onFilter: (value, record) =>
        value === "disabled" ? !!record.isDisabled : !record.isDisabled,
    },
    {
      title: "Link",
      dataIndex: "slug",
      key: "slug",
      render: (slug, record) => {
        if (record.isDisabled) {
          return <IdcardOutlined style={{ color: "#bfbfbf" }} />;
        }

        return (
          <Link
            to={{ pathname: `${config.cardsUrl[env]}/${slug}` }}
            target="_blank"
          >
            <IdcardOutlined />
          </Link>
        );
      },
    },
    {
      title: "Azioni",
      width: 320,
      render: (_, record) => {
        const cardProjectId = {
          dev: "",
          prod: "SalPMFcaAg",
        };
        const giottoUrl = `${config.giottoUrl[env]}/start?tipo=${
          cardProjectId[env]
        }&servizio=${encodeURIComponent(record.slug)}`;
        return (
          <Space wrap>
            {env === "prod" && (
              <Link
                to={{ pathname: giottoUrl }}
                target="_blank"
              >
                <Button size="small" type="primary" ghost>
                  Crea Card
                </Button>
              </Link>
            )}
            <Button size="small" onClick={() => copyInfo(record)}>
              Duplica
            </Button>
            <Button
              size="small"
              onClick={() => confirmToggleDisabled(record)}
            >
              {record.isDisabled ? "Riattiva" : "Disattiva"}
            </Button>
            <Button
              danger
              size="small"
              onClick={() => confirmDeleteCard(record)}
            >
              Elimina
            </Button>
          </Space>
        );
      },
    },
  ];

  const confirmToggleDisabled = (record) => {
    const nextDisabled = !record.isDisabled;

    Modal.confirm({
      title: nextDisabled ? "Disattivare questa card?" : "Riattivare questa card?",
      content: nextDisabled
        ? "Il link pubblico non sarà più raggiungibile. L'utente potrà accedere all'area personale solo per vedere l'avviso di contatto."
        : "Il link pubblico tornerà raggiungibile.",
      okText: nextDisabled ? "Disattiva" : "Riattiva",
      cancelText: "Annulla",
      okButtonProps: {
        danger: nextDisabled,
      },
      onOk: async () => {
        const updatedCard = await patchCard(record.slug, {
          isDisabled: nextDisabled,
        });

        updateCardInList(record.slug, updatedCard);
        message.success(nextDisabled ? "Card disattivata." : "Card riattivata.");
      },
    });
  };

  const confirmDeleteCard = (record) => {
    Modal.confirm({
      title: "Eliminare definitivamente questa card?",
      content:
        "La card sparirà dall'admin e il link pubblico non sarà più raggiungibile.",
      okText: "Elimina",
      cancelText: "Annulla",
      okButtonProps: {
        danger: true,
      },
      onOk: async () => {
        await deleteCard(record.slug);
        removeCardFromList(record.slug);
        message.success("Card eliminata.");
      },
    });
  };

  const copyInfo = (record) => {
    const copyInfo = {
      ...record,
      firstName: "",
      lastName: "",
      role: "",
      mobile: "",
      email: "",
      avatarUrl: "",
      slug: "",
      id: "",
      createdAt: "",
      updatedAt: "",
      lastUpdate: "",
      originalSlug: "",
      companyBillingName: undefined,
      billingCompanyName:
        record.billingCompanyName || record.companyBillingName || "",
    };

    history.push("/new", copyInfo);
  };

  const handleClose = () => {
    setVisible(false);
  };

  return (
    <div style={{ margin: "16px 16px" }}>
      {visible ? (
        <Alert
          message="Attenzione: Stai lavorando nell'ambiente di sviluppo"
          banner
          closable
          afterClose={handleClose}
        />
      ) : null}
      <div style={{ marginBottom: "16px" }}>
        {loading && <LinearProgress />}
      </div>
      {/* <Divider /> */}
      <Title level={3} style={{ marginBottom: "40px" }}>
        <Row justify="space-between">
          <Col span={5}>
            <span style={{ marginRight: "20px" }}>Lista Cards</span>
            <Badge overflowCount={5000} count={filteredItems.length} style={{ backgroundColor: '#6574a1' }}/>
          </Col>
          <Col span={3}>
            <Button type="primary" onClick={() => history.push("/new")}>
              Aggiungi
            </Button>
          </Col>
        </Row>
      </Title>
      <div
        style={{
          marginBottom: 16,
          padding: 16,
          background: "#fff",
          border: "1px solid #f0f0f0",
          borderRadius: 4,
        }}
      >
        <Row gutter={[12, 12]} align="bottom">
          <Col xs={24} sm={12} md={8} lg={4}>
            <Typography.Text strong>Nome</Typography.Text>
            <Input
              allowClear
              placeholder="Nome o cognome"
              value={tableFilters.name}
              onChange={(event) =>
                updateTableFilter("name", event.target.value)
              }
            />
          </Col>
          <Col xs={24} sm={12} md={8} lg={5}>
            <Typography.Text strong>Impresa</Typography.Text>
            <Input
              allowClear
              placeholder="Nome impresa"
              value={tableFilters.companyName}
              onChange={(event) =>
                updateTableFilter("companyName", event.target.value)
              }
            />
          </Col>
          <Col xs={24} sm={12} md={8} lg={4}>
            <Typography.Text strong>Piano</Typography.Text>
            <Select
              allowClear
              placeholder="Tutti"
              style={{ width: "100%" }}
              value={tableFilters.plan}
              onChange={(value) => updateTableFilter("plan", value)}
            >
              <Select.Option value="gratis">Gratis</Select.Option>
              <Select.Option value="plus">Plus</Select.Option>
            </Select>
          </Col>
          <Col xs={24} sm={12} md={10} lg={5}>
            <Typography.Text strong>Data creazione</Typography.Text>
            <RangePicker
              style={{ width: "100%" }}
              format="DD/MM/YYYY"
              value={tableFilters.createdAtRange}
              onChange={(value) => updateTableFilter("createdAtRange", value)}
            />
          </Col>
          <Col xs={24} sm={12} md={8} lg={4}>
            <Typography.Text strong>Coupon</Typography.Text>
            <Input
              allowClear
              placeholder="Codice coupon"
              value={tableFilters.couponCode}
              onChange={(event) =>
                updateTableFilter("couponCode", event.target.value)
              }
            />
          </Col>
          <Col xs={24} sm={12} md={4} lg={2}>
            <Button style={{ width: "100%" }} onClick={resetTableFilters}>
              Reset
            </Button>
          </Col>
        </Row>
      </div>
      <Table
        dataSource={filteredItems}
        columns={columns}
        loading={loading}
        rowKey="id"
        scroll={{ x: 1300 }}
        expandable={{
          expandedRowRender: (record) => {
            return (
              <div>
                <div>
                  <h3>Anagrafica</h3>
                  {record.firstName && (
                    <div>
                      <strong>Nome</strong>: {record.firstName}
                    </div>
                  )}
                  {record.lastName && (
                    <div>
                      <strong>Cognome</strong>: {record.lastName}
                    </div>
                  )}
                  {record.mobile && (
                    <div>
                      <strong>Cellulare</strong>: {record.mobile}
                    </div>
                  )}
                  {record.email && (
                    <div>
                      <strong>Email</strong>: {record.email}
                    </div>
                  )}
                  {record.role && (
                    <div>
                      <strong>Ruolo</strong>: {record.role}
                    </div>
                  )}
                  {record.avatarUrl && (
                    <div>
                      <strong>Avatar</strong>: {record.avatarUrl}
                    </div>
                  )}
                </div>
                <div style={{ marginTop: "30px" }}>
                  <h3>Dettagli Azienda:</h3>
                  {record.companyName && (
                    <div>
                      <strong>Impresa</strong>: {record.companyName}
                    </div>
                  )}
                  {record.phone && (
                    <div>
                      <strong>Telefono ufficio</strong>: {record.phone}
                    </div>
                  )}
                  {record.companyEmail && (
                    <div>
                      <strong>Email ufficio</strong>: {record.companyEmail}
                    </div>
                  )}
                  {record.city && (
                    <div>
                      <strong>Città</strong>: {record.city}
                    </div>
                  )}
                  {record.zipCode && (
                    <div>
                      <strong>CAP</strong>: {record.zipCode}
                    </div>
                  )}
                  {record.address && (
                    <div>
                      <strong>Indirizzo</strong>: {record.address}
                    </div>
                  )}
                  {record.description && (
                    <div>
                      <strong>Descrizione</strong>: {record.description}
                    </div>
                  )}
                  {record.website && (
                    <div>
                      <strong>Sito</strong>: {record.website}
                    </div>
                  )}
                  {record.logoUrl && (
                    <div>
                      <strong>Logo</strong>: {record.logoUrl}
                    </div>
                  )}
                  {record.linkedin && (
                    <div>
                      <strong>LinkedIn</strong>: {record.linkedin}
                    </div>
                  )}
                  {record.facebook && (
                    <div>
                      <strong>Facebook</strong>: {record.facebook}
                    </div>
                  )}
                  {record.instagram && (
                    <div>
                      <strong>Instagram</strong>: {record.instagram}
                    </div>
                  )}
                  {record.twitter && (
                    <div>
                      <strong>X / Twitter</strong>: {record.twitter}
                    </div>
                  )}
                  {record.youtube && (
                    <div>
                      <strong>YouTube</strong>: {record.youtube}
                    </div>
                  )}
                  {record.tiktok && (
                    <div>
                      <strong>TikTok</strong>: {record.tiktok}
                    </div>
                  )}
                </div>
                <div style={{ marginTop: "30px" }}>
                  <h3>Dati di fatturazione:</h3>
                  {(record.billingCompanyName || record.companyBillingName) && (
                    <div>
                      <strong>Ragione Sociale</strong>:{" "}
                      {record.billingCompanyName || record.companyBillingName}
                    </div>
                  )}
                  {record.billingAddress && (
                    <div>
                      <strong>Indirizzo di fatturazione</strong>:{" "}
                      {record.billingAddress}
                    </div>
                  )}
                  {record.billingCity && (
                    <div>
                      <strong>Città di fatturazione</strong>:{" "}
                      {record.billingCity}
                    </div>
                  )}
                  {record.billingZipCode && (
                    <div>
                      <strong>CAP di fatturazione</strong>:{" "}
                      {record.billingZipCode}
                    </div>
                  )}
                  {record.vat && (
                    <div>
                      <strong>P.IVA</strong>: {record.vat}
                    </div>
                  )}
                  {record.pec && (
                    <div>
                      <strong>PEC</strong>: {record.pec}
                    </div>
                  )}
                  {record.electronicInvoicingId && (
                    <div>
                      <strong>Codice Destinatario</strong>:{" "}
                      {record.electronicInvoicingId}
                    </div>
                  )}
                </div>
                <div style={{ marginTop: "30px" }}>
                  <h3>Altro:</h3>
                  <div>
                    <strong>Piano</strong>: {record.plan || "gratis"}
                    {record.planStatus ? ` · ${record.planStatus}` : ""}
                  </div>
                  <div>
                    <strong>Stato</strong>:{" "}
                    {record.isDisabled ? "Disattivata" : "Attiva"}
                  </div>
                  {record.couponCode && (
                    <div>
                      <strong>Coupon</strong>: {record.couponCode}
                    </div>
                  )}
                  {record.slug && (
                    <div>
                      <strong>Slug</strong>: {record.slug}
                    </div>
                  )}
                  {record.createdAt && (
                    <div>
                      <strong>Data di creazione</strong>: {record.createdAt}
                    </div>
                  )}
                  {record.updatedAt && (
                    <div>
                      <strong>Data di aggiornamento</strong>: {record.updatedAt}
                    </div>
                  )}
                  {record.id && (
                    <div>
                      <strong>ID</strong>: {record.id}
                    </div>
                  )}
                </div>
              </div>
            );
          },
        }}
      />
    </div>
  );
};

export default Cards;
